import React, { useContext, useEffect, useMemo, useState } from 'react'
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import useFetch, { CachePolicies } from 'use-http'
import { colors } from '../../../../ui-kit'
import { UserContext } from '../../../providers'
import useToast from '../../../hooks/useToast'

interface ExtensionPreset {
  id: string
  name: string
  description: string
  config: Record<string, any>
}

interface SavedExtensionsSectionProps {
  values: any
  handleChange: (event: any) => void
  form: any
  isDisabled?: boolean
  fieldPath?: string // Optional custom field path (e.g., 'upload.savedExtensions' or 'savedExtensions')
}

const SavedExtensionsSection: React.FC<SavedExtensionsSectionProps> = ({
  values,
  handleChange,
  form,
  isDisabled = false,
  fieldPath,
}) => {
  const { t } = useTranslation()
  const { user } = useContext(UserContext)
  const toast = useToast()
  const [showAll, setShowAll] = useState(false)
  const [savedExtensions, setSavedExtensions] = useState<ExtensionPreset[]>([])

  const { request, loading } = useFetch<ExtensionPreset[]>({
    cachePolicy: CachePolicies.NO_CACHE,
  })

  useEffect(() => {
    const fetchSavedExtensions = async () => {
      const response = await request.get(
        `/api/v1/saved-extensions`,
      )

      if (request.error) {
        toast({
          description: t(
            'error_fetching_saved_extensions',
            'Error fetching saved extensions. Try refreshing the page.',
          ),
          status: 'error',
          isClosable: true,
          duration: 5000,
          position: 'top-right',
          variant: 'outline',
        })
      } else {
        setSavedExtensions(response)
      }
    }

    fetchSavedExtensions()
  }, [])

  // Determine the actual field path and value location
  const actualFieldPath = fieldPath || 'upload.savedExtensions'
  
  const selectedExtensions = useMemo(() => {
    // Support both nested (upload.savedExtensions) and direct (savedExtensions) paths
    if (actualFieldPath === 'upload.savedExtensions') {
      return values?.upload?.savedExtensions || []
    } else {
      return values?.savedExtensions || []
    }
  }, [values, actualFieldPath])

  const handleExtensionChange = (extensionId: string, checked: boolean) => {
    let updatedExtensions = [...selectedExtensions]

    if (checked) {
      // Add extension if not already present
      const extensionExists = updatedExtensions.some((ext: any) => ext.id === extensionId)
      if (!extensionExists) {
        updatedExtensions.push({
          name: 'saved-extension',
          id: extensionId,
        })
      }
    } else {
      // Remove extension
      updatedExtensions = updatedExtensions.filter((ext: any) => ext.id !== extensionId)
    }

    handleChange({
      target: {
        value: updatedExtensions,
        name: actualFieldPath,
      },
    })
  }

  const displayedExtensions = useMemo(() => {
    if (showAll) return savedExtensions
    return savedExtensions.slice(0, 3)
  }, [savedExtensions, showAll])

  const remainingCount = savedExtensions.length - 3

  if (loading) {
    return (
      <VStack width="100%" alignItems="flex-start">
        <FormLabel fontWeight={700}>
          {t('saved_extensions', 'Saved Extensions')}
        </FormLabel>
        <Spinner size="sm" />
      </VStack>
    )
  }

  if (savedExtensions.length === 0) {
    return null
  }

  return (
    <VStack width="100%" alignItems="flex-start">
      <FormControl
        isInvalid={Boolean(
          actualFieldPath === 'upload.savedExtensions'
            ? form.errors?.upload?.savedExtensions && form.touched?.upload?.savedExtensions
            : form.errors?.savedExtensions && form.touched?.savedExtensions,
        )}
      >
        <FormLabel fontWeight={700}>
          {t('saved_extensions', 'Saved Extensions')} ({savedExtensions.length}{' '}
          {t('available', 'available')})
        </FormLabel>

        <VStack alignItems="flex-start" spacing="1em">
          {displayedExtensions.map(extension => (
            <Checkbox
              key={extension.id}
              isChecked={selectedExtensions.some((ext: any) => ext.id === extension.id)}
              isDisabled={isDisabled}
              onChange={e => handleExtensionChange(extension.id, e.target.checked)}
            >
              <Text>{extension.name}</Text>
            </Checkbox>
          ))}

          {!showAll && remainingCount > 0 && (
            <Button
              variant="link"
              color={colors.appBlue}
              size="sm"
              onClick={() => setShowAll(true)}
              isDisabled={isDisabled}
            >
              {t('show_more', 'Show {{count}} more...', { count: remainingCount })}
            </Button>
          )}

          {showAll && savedExtensions.length > 3 && (
            <Button
              variant="link"
              color={colors.appBlue}
              size="sm"
              onClick={() => setShowAll(false)}
              isDisabled={isDisabled}
            >
              {t('show_less', 'Show less')}
            </Button>
          )}
        </VStack>

        <FormErrorMessage>
          {actualFieldPath === 'upload.savedExtensions'
            ? form.errors?.upload?.savedExtensions
            : form.errors?.savedExtensions}
        </FormErrorMessage>
      </FormControl>
    </VStack>
  )
}

export default SavedExtensionsSection
